import { useEffect, useState } from "react";
import { Inbox, Check, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { fetchAccessRequests, approveAccessRequest, dismissAccessRequest } from "../../lib/access";

/**
 * Pending "Request access" queue shown on Config → Users. Admins approve
 * (auto-grants the page as a per-user override) or dismiss. Calls onResolved so
 * the parent can refresh the user table (Custom badge updates).
 */
export default function AccessRequestsPanel({ onResolved }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchAccessRequests("pending");
      setRequests(data.requests || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const act = async (id, kind) => {
    setBusy(id);
    try {
      if (kind === "approve") {
        const res = await approveAccessRequest(id);
        toast.success(`Granted ${res.page_id} to ${res.target}`);
      } else {
        await dismissAccessRequest(id);
        toast.success("Request dismissed");
      }
      setRequests((r) => r.filter((x) => x.id !== id));
      onResolved?.();
    } catch { toast.error("Action failed"); }
    finally { setBusy(null); }
  };

  if (!loading && requests.length === 0) return null;

  return (
    <div className="card-dark p-4 mb-5 border-orange-400/20" data-testid="access-requests-panel">
      <div className="flex items-center gap-2 mb-3">
        <Inbox className="w-4 h-4 text-orange-300" />
        <h3 className="text-sm font-semibold text-white flex-1">
          Access requests
          {requests.length > 0 && (
            <span className="ml-2 text-[11px] font-mono px-1.5 py-0.5 rounded-full bg-orange-500/15 text-orange-200 border border-orange-400/30">
              {requests.length} pending
            </span>
          )}
        </h3>
        <button onClick={load} className="text-slate-400 hover:text-white p-1" title="Refresh" data-testid="access-requests-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      {loading ? (
        <div className="text-slate-500 text-sm py-3">Loading…</div>
      ) : (
        <div className="space-y-2">
          {requests.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`access-request-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-100 truncate">
                  <b>{r.user_name || r.user_email}</b>
                  <span className="text-slate-500"> wants </span>
                  <span className="text-orange-200">{r.page_label}</span>
                </div>
                <div className="text-[11px] font-mono text-slate-500 truncate">{r.user_email} · {r.page_id}</div>
              </div>
              <button
                onClick={() => act(r.id, "approve")}
                disabled={busy === r.id}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-40"
                data-testid={`access-request-approve-${r.id}`}
              >
                <Check className="w-3.5 h-3.5" /> Grant
              </button>
              <button
                onClick={() => act(r.id, "dismiss")}
                disabled={busy === r.id}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-40"
                data-testid={`access-request-dismiss-${r.id}`}
              >
                <X className="w-3.5 h-3.5" /> Dismiss
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
